import React, { useState, useEffect, useRef } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getProfileCompanyPicUrl, getApiUrl, COMPANY_API } from '../../helpers/url_helper';
import { dataAPI } from '../../helpers/api_service';
import { formatDate, formatDateToAPI } from '../../helpers/UtilsFunctions';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarIcon from '../../assets/Calendar.svg';
import DescriptionIcon from '../../assets/Description.svg';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import AttachIcon from '../../assets/Attach.svg';
import PDFIcon from '../../assets/PDF.svg';
import EyeOpenIcon from '../../assets/eye-open.svg';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import TaskDetailCommentBlock from './TaskDetailCommentBlock';
import icPriority from '../../assets/Priority.svg';
import status_cat from '../../assets/status_cat.svg';
import UserIcon from '../../assets/user.svg';
import Users2Icon from '../../assets/Users_2.svg';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { Warning as WarningIcon } from '@mui/icons-material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { FormControl, Select, MenuItem } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import TextField from '@mui/material/TextField';
import MessageAlert from '../Common/MessageAlert';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { enGB } from 'date-fns/locale';
import './Projects.css';

// Helper function to handle profile picture URL (same as DashboardLayout)
const getProfilePicUrl = (profilePic) => {
  if (!profilePic) return '';

  // Check if URL already starts with http (any protocol)
  if (profilePic.toLowerCase().startsWith('http://') || profilePic.toLowerCase().startsWith('https://')) {
    return profilePic; // Return as is if it's already a full URL
  } else {
    return getProfileCompanyPicUrl(profilePic); // Append profile/company pic URL prefix if it's a relative path
  }
};

const TaskDetails = () => {
  const { mode } = useThemeMode();
  const { hasEditPermission, hasAddPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const { taskId, projectId } = useParams();
  const [task, setTask] = useState(null);
  const [projectDetails, setProjectDetails] = useState(null);
  const [actualProjectId, setActualProjectId] = useState(projectId); // Real project_id from API
  const [projectStartDate, setProjectStartDate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);

  // Edit mode state
  const [editTaskDetails, setEditTaskDetails] = useState(false);
  const [editDescription, setEditDescription] = useState(false);
  const [editData, setEditData] = useState({});

  // Data for dropdowns - same as TaskForm
  const [employees, setEmployees] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [isLoadingEnums, setIsLoadingEnums] = useState(false);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);

  // File handling state
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [viewingAttachmentId, setViewingAttachmentId] = useState(null);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  // Live count-up timer for "Elapsed time" when session is active (starts from total_duration_minutes or 0)
  const [sessionBaseMinutes, setSessionBaseMinutes] = useState(0);
  const [sessionBaseTime, setSessionBaseTime] = useState(() => Date.now());
  const [liveTick, setLiveTick] = useState(() => Date.now());

  // Time logs modal
  const [timelogModalOpen, setTimelogModalOpen] = useState(false);
  const [timelogs, setTimelogs] = useState([]);
  const [timelogsLoading, setTimelogsLoading] = useState(false);
  const [editingTimelog, setEditingTimelog] = useState(null); // { id, startInput, endInput }
  const [isSavingTimelog, setIsSavingTimelog] = useState(false);
  const [timelogEditError, setTimelogEditError] = useState('');
  const [timelogEdited, setTimelogEdited] = useState(false);

  // Handle download all attachments
  const handleDownloadAll = async () => {
    if (!taskId || !task?.attachments || task.attachments.length === 0) {
      setUploadError('No attachments available to download.');
      setTimeout(() => {
        setUploadError('');
      }, 5000);
      return;
    }

    // Clear existing messages
    setUploadSuccess('');
    setUploadError('');
    setIsDownloadingAll(true);

    try {
      const response = await fetch(
        getApiUrl(`${COMPANY_API.TASK_ATTACHMENT_DOWNLOAD_ALL}/${taskId}`),
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          // Ignore JSON parse errors and use default message
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.status === 'success' && result.data?.attachments && result.data.attachments.length > 0) {
        const attachments = result.data.attachments;

        // Use direct URL (no fetch) – S3 pre-signed URLs lack CORS, so we trigger download via anchor.
        // The API URLs use response-content-disposition=attachment so the browser downloads instead of opening.
        for (let i = 0; i < attachments.length; i++) {
          const attachment = attachments[i];
          const fileName = attachment.file_name || `attachment_${attachment.attachment_id}`;
          const link = document.createElement('a');
          link.href = attachment.download_url;
          link.download = fileName;
          link.setAttribute('download', fileName);
          link.style.display = 'none';
          link.style.visibility = 'hidden';
          link.style.position = 'absolute';
          link.target = '_self';
          link.rel = 'noopener noreferrer';
          document.body.appendChild(link);
          link.click();
          if (link.parentNode) {
            document.body.removeChild(link);
          }
          // Delay between downloads so the browser doesn’t block or merge multiple downloads
          if (i < attachments.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 800));
          }
        }

        setUploadSuccess(`Downloaded ${attachments.length} file(s).`);
        setTimeout(() => {
          setUploadSuccess('');
        }, 3000);
      } else {
        throw new Error(result.message || 'No attachments found to download.');
      }
    } catch (error) {
      console.error('Download all attachments error:', error);
      setUploadError(error.message || 'Failed to download attachments. Please try again.');
      setTimeout(() => {
        setUploadError('');
      }, 5000);
    } finally {
      setIsDownloadingAll(false);
    }
  };

  const handleViewAttachment = async (attachment) => {
    if (!attachment?.id) {
      setUploadError('Attachment is missing required information.');
      setTimeout(() => {
        setUploadError('');
      }, 5000);
      return;
    }

    // Clear existing messages
    setUploadSuccess('');
    setUploadError('');

    setViewingAttachmentId(attachment.id);

    try {
      const response = await fetch(
        getApiUrl(`${COMPANY_API.TASK_ATTACHMENT_DOWNLOAD}/${attachment.id}`),
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          // Ignore JSON parse errors and use default message
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.status === 'success' && result.data?.download_url) {
        window.open(result.data.download_url, '_blank', 'noopener,noreferrer');
      } else {
        throw new Error(result.message || 'Failed to generate attachment link.');
      }
    } catch (error) {
      console.error('View attachment error:', error);
      setUploadError(error.message || 'Failed to open attachment. Please try again.');
      setTimeout(() => {
        setUploadError('');
      }, 5000);
    } finally {
      setViewingAttachmentId(null);
    }
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get project start date from navigation state
  useEffect(() => {
    if (location.state?.projectStartDate) {
      setProjectStartDate(location.state.projectStartDate);
    }
  }, [location.state]);

  // Fetch task enums and employees - same as TaskForm
  useEffect(() => {
    const fetchTaskEnums = async () => {
      setIsLoadingEnums(true);
      try {
        const response = await dataAPI.getTaskEnums();
        console.log('TaskDetails - Task Enums Response:', response);
        if (response.priorities && response.statuses) {
          console.log('TaskDetails - Priorities:', response.priorities);
          console.log('TaskDetails - Statuses:', response.statuses);
          setPriorityOptions(response.priorities);
          setStatusOptions(response.statuses);

          // Store task enums in localStorage for use in other components
          localStorage.setItem('taskEnums', JSON.stringify({
            priorities: response.priorities,
            statuses: response.statuses
          }));
        }
      } catch (error) {
        console.error('Error fetching task enums:', error);
        // Fallback to localStorage if API fails
        try {
          const storedEnums = localStorage.getItem('taskEnums');
          if (storedEnums) {
            const parsedEnums = JSON.parse(storedEnums);
            console.log('TaskDetails - Using localStorage fallback:', parsedEnums);
            setPriorityOptions(parsedEnums.priorities || []);
            setStatusOptions(parsedEnums.statuses || []);
          }
        } catch (localStorageError) {
          console.error('Error reading from localStorage:', localStorageError);
        }
      } finally {
        setIsLoadingEnums(false);
      }
    };

    const fetchEmployees = async () => {
      setIsLoadingEmployees(true);
      try {
        const response = await dataAPI.getAllEmployees();
        setEmployees(response.data || []);
      } catch (error) {
        console.error('Error fetching employees:', error);
      } finally {
        setIsLoadingEmployees(false);
      }
    };

    // Fetch both enums and employees
    fetchTaskEnums();
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (taskId) {
      fetchTaskDetails();
    }
  }, [taskId]);

  // Get current user ID from localStorage
  useEffect(() => {
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsedUserData = JSON.parse(userData);
        setCurrentUserId(parsedUserData.id || parsedUserData.user_id);
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
  }, []);

  // Check if current user can edit the task (for edit buttons - creator or when creator = assigned)
  const canUserEditTask = () => {
    if (!currentUserId || !task) return false;

    // First check if user has edit permission from role
    if (!hasEditPermission('task')) return false;

    // Creator can always edit everything - check multiple possible field names
    const isCreator = task.created_by_user_id === currentUserId ||
      task.created_by === currentUserId ||
      task.assigned_by === currentUserId;

    // If creator and assigned user are the same, they can edit everything
    const isSameUser = (task.created_by_user_id === currentUserId ||
      task.created_by === currentUserId ||
      task.assigned_by === currentUserId) &&
      task.assigned_to === currentUserId;

    const canEdit = isCreator || isSameUser;

    return canEdit;
  };

  // Check if current user can change status (creator, assigned user, or when creator = assigned)
  const canUserChangeStatus = () => {
    if (!currentUserId || !task) return false;

    // Creator can always change status - check multiple possible field names
    const isCreator = task.created_by_user_id === currentUserId ||
      task.created_by === currentUserId ||
      task.assigned_by === currentUserId;

    // Assigned user can change status
    const isAssigned = task.assigned_to === currentUserId;

    // If creator and assigned user are the same, they can do everything
    const isSameUser = (task.created_by_user_id === currentUserId ||
      task.created_by === currentUserId ||
      task.assigned_by === currentUserId) &&
      task.assigned_to === currentUserId;

    return isCreator || isAssigned || isSameUser;
  };

  const fetchTaskDetails = async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
    }
    setError('');
    try {
      // Fetch task details directly using the task ID
      const taskResponse = await dataAPI.getTaskDetail(taskId);

      if (taskResponse.status === 'success') {
        setTask(taskResponse.data);

        // Extract real project_id from API response (prioritize API over URL params)
        const apiProjectId = taskResponse.project_details?.id || taskResponse.data?.project_id;
        if (apiProjectId) {
          setActualProjectId(apiProjectId);
          console.log(`🎯 TaskDetails: Using API project_id: ${apiProjectId} (URL param was: ${projectId})`);
        }

        // Set project details if available in the response
        if (taskResponse.project_details) {
          setProjectDetails({
            id: taskResponse.project_details.id || actualProjectId,
            title: taskResponse.project_details.title || 'Project Title',
            description: taskResponse.project_details.description || '',
            start_date: taskResponse.project_details.start_date || '',
            end_date: taskResponse.project_details.end_date || '',
            status: taskResponse.project_details.status || '',
            created_by_username: taskResponse.project_details.creator_username || 'Unknown',
            created_at: taskResponse.project_details.created_at || '',
            updated_at: taskResponse.project_details.updated_at || ''
          });
        } else {
          // Fallback to basic project details
          setProjectDetails({
            id: actualProjectId,
            title: 'Project Title',
            description: '',
            start_date: '',
            end_date: '',
            status: '',
            created_by_username: 'Unknown',
            created_at: '',
            updated_at: ''
          });
        }
      } else {
        setError('Failed to load task details');
      }
    } catch (error) {
      console.error('Error fetching task details:', error);
      setError('Failed to load task details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    // Check if we came from dashboard
    if (location.state?.fromDashboard) {
      // Navigate back to dashboard
      navigate('/dashboard');
    } else if (location.state?.fromManageTasks) {
      // Navigate back to manage tasks page
      navigate('/manage-task');
    } else {
      // Navigate back to project tasks (original behavior)
      // Use actualProjectId (from API) instead of URL params
      if (actualProjectId) {
        navigate(`/project/${actualProjectId}/tasks`);
      } else {
        // Fallback to dashboard if no project ID is available
        navigate('/dashboard');
      }
    }
  };

  // Edit functionality
  const toggleEditMode = (field) => {
    console.log('toggleEditMode called with field:', field);
    console.log('Current editTaskDetails:', editTaskDetails);
    console.log('Current editDescription:', editDescription);
    console.log('Current task data:', task);
    console.log('Current priorityOptions:', priorityOptions);
    console.log('Current statusOptions:', statusOptions);

    if (field === 'description') {
      // For description editing
      if (!editDescription) {
        // If task details is in edit mode, cancel it first
        if (editTaskDetails) {
          console.log('Canceling task details edit mode before entering description edit mode');
          setEditTaskDetails(false);
          setEditData({});
        }
        // Entering edit mode - populate description
        console.log('Entering description edit mode');
        setEditData({
          description: task?.description || ''
        });
      }
      setEditDescription(!editDescription);
      console.log('Description edit mode set to:', !editDescription);
    } else {
      // For task details editing
      if (!editTaskDetails) {
        // If description is in edit mode, cancel it first
        if (editDescription) {
          console.log('Canceling description edit mode before entering task details edit mode');
          setEditDescription(false);
          setEditData({});
        }
        // Entering edit mode - populate all fields
        console.log('Entering task details edit mode');
        setEditData({
          title: task?.title || '',
          description: task?.description || '',
          assigned_to: task?.assigned_to || '',
          priority: task?.priority || '',
          status: task?.status || '',
          start_date: task?.start_date || '',
          due_date: task?.due_date || ''
        });
        console.log('Edit data populated:', {
          title: task?.title || '',
          description: task?.description || '',
          assigned_to: task?.assigned_to || '',
          priority: task?.priority || '',
          status: task?.status || '',
          start_date: task?.start_date || '',
          due_date: task?.due_date || ''
        });
      }
      setEditTaskDetails(!editTaskDetails);
      console.log('Task details edit mode set to:', !editTaskDetails);
    }
  };

  const handleEditDataChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (field) => {
    // Clear any existing messages
    setUploadSuccess('');
    setUploadError('');

    // Validation for task details
    if (field === 'taskDetails') {
      // Check if title is empty
      if (!editData.title || editData.title.trim() === '') {
        setUploadError('Task title is required and cannot be empty');
        setTimeout(() => {
          setUploadError('');
        }, 5000);
        return;
      }

      // Helper function to normalize date to midnight for date-only comparison
      const normalizeDateToMidnight = (date) => {
        if (!date) return null;
        const normalized = new Date(date);
        normalized.setHours(0, 0, 0, 0);
        return normalized;
      };

      // Check date validation (past dates allowed for both start and due)
      // Check if task start date is later than due date
      if (editData.start_date && editData.due_date) {
        const taskStartDate = normalizeDateToMidnight(editData.start_date);
        const taskDueDate = normalizeDateToMidnight(editData.due_date);
        if (taskStartDate > taskDueDate) {
          setUploadError('Start date cannot be later than due date');
          setTimeout(() => {
            setUploadError('');
          }, 5000);
          return;
        }
      }

      // Due date: past dates are allowed (no validation that due date must be today or future)
    }

    // Set updating state
    setIsUpdating(true);

    try {
      const formDataToSend = new FormData();
      if (field === 'description') {
        formDataToSend.append('description', editData.description || '');
      } else {
        formDataToSend.append('title', editData.title);
        formDataToSend.append('description', editData.description || '');
        formDataToSend.append('assigned_to', editData.assigned_to || '');
        formDataToSend.append('priority', editData.priority);
        // Use task.status instead of editData.status since status changes update task.status directly
        formDataToSend.append('status', task.status || editData.status || '');
        formDataToSend.append('start_date', editData.start_date || '');
        formDataToSend.append('due_date', editData.due_date || '');
      }
      formDataToSend.append('project_id', actualProjectId || projectId);

      const response = await fetch(getApiUrl(`${COMPANY_API.TASK_EDIT}/${taskId}`), {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: formDataToSend
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Task updated successfully:', result);

      // Update local task data with edited values
      if (field === 'description') {
        console.log('Updating description:', editData.description);
        setTask(prev => {
          const updated = {
            ...prev,
            description: editData.description || ''
          };
          console.log('Updated task for description:', updated);
          return updated;
        });
      } else {
        // For task details, we need to update the fields that are actually displayed
        console.log('Updating task details:', editData);
        console.log('Current employees:', employees);
        console.log('Finding employee for assigned_to:', editData.assigned_to);

        setTask(prev => {
          const updated = {
            ...prev,
            title: editData.title || '',
            description: editData.description || '',
            assigned_to: editData.assigned_to || '',
            priority: editData.priority || '',
            // Use task.status to preserve any status changes made before save
            status: task.status || editData.status || prev.status || '',
            start_date: editData.start_date || '',
            due_date: editData.due_date || '',
            // Also update the assigned_user_details if we have the assigned_to value
            ...(editData.assigned_to && {
              assigned_user_details: {
                ...prev.assigned_user_details,
                id: editData.assigned_to,
                // We'll need to find the employee name from the employees list
                full_name: employees.find(emp => emp.id === editData.assigned_to)?.full_name || 'Unknown'
              }
            })
          };
          console.log('Updated task for taskDetails:', updated);
          return updated;
        });
      }

      // Exit edit mode
      if (field === 'description') {
        setEditDescription(false);
        setEditData({});
        console.log('Description edit mode exited');
      } else {
        setEditTaskDetails(false);
        setEditData({});
        console.log('Task details edit mode exited');
      }

      // Show success message
      if (field === 'description') {
        setUploadSuccess('Description updated successfully!');
      } else {
        setUploadSuccess('Task details updated successfully!');
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setUploadSuccess('');
      }, 3000);

      // Log the final task state
      setTimeout(() => {
        console.log('Final task state after update:', task);
      }, 100);

    } catch (error) {
      console.error('Update task error:', error);
      setUploadError(`Failed to update task: ${error.message}`);
      // Clear error message after 5 seconds
      setTimeout(() => {
        setUploadError('');
      }, 5000);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = (field) => {
    if (field === 'description') {
      setEditDescription(false);
      setEditData({});
    } else {
      setEditTaskDetails(false);
      setEditData({});
    }
  };

  // Function to refresh just attachments without showing loading state
  const refreshAttachments = async () => {
    try {
      const taskResponse = await dataAPI.getTaskDetail(taskId);
      if (taskResponse.status === 'success' && taskResponse.data.attachments) {
        setTask(prev => ({
          ...prev,
          attachments: taskResponse.data.attachments
        }));
      }
    } catch (error) {
      console.error('Error refreshing attachments:', error);
      // Don't show error to user for background refresh
    }
  };

  // Handle file selection and upload
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Clear any existing messages
    setUploadSuccess('');
    setUploadError('');

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File size must be less than 10MB');
      // Clear error message after 5 seconds
      setTimeout(() => {
        setUploadError('');
      }, 5000);
      return;
    }

    setSelectedFile(file);
    setIsUploading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('attachments', file);
      formDataToSend.append('project_id', actualProjectId || projectId); // Use actualProjectId from API

      const response = await fetch(getApiUrl(`${COMPANY_API.TASK_EDIT}/${taskId}`), {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: formDataToSend
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('File uploaded successfully:', result);

      // Update local state immediately instead of refreshing
      const newAttachment = {
        id: result.data?.id || Date.now(), // Use API response ID or fallback
        file_name: file.name,
        file_path: result.data?.file_path || '',
        uploaded_at: new Date().toISOString(),
        uploaded_by: currentUserId || 'Unknown'
      };

      setTask(prev => ({
        ...prev,
        attachments: [newAttachment, ...(prev.attachments || [])]
      }));

      // Clear the file input
      event.target.value = '';
      setSelectedFile(null);

      // Show success message using MessageAlert
      setUploadSuccess('File uploaded successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setUploadSuccess('');
      }, 3000);

    } catch (error) {
      console.error('File upload error:', error);
      // Show error message using MessageAlert instead of default alert
      setUploadError(`Failed to upload file: ${error.message}`);
      // Clear error message after 5 seconds
      setTimeout(() => {
        setUploadError('');
      }, 5000);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle status change - only update local state, will be saved with save button
  const handleStatusChange = (event) => {
    const newStatus = event.target.value;
    if (!newStatus || newStatus === task.status) return;

    // Clear any existing messages
    setUploadSuccess('');
    setUploadError('');

    // Update local state only - will be saved with save button
    setTask(prev => ({
      ...prev,
      status: newStatus
    }));

    console.log('Status changed locally to:', newStatus, '- will be saved with save button');
  };

  // Handle start task - POST company/tasks/start/:taskId (starts timelog session)
  const handleStartTask = async () => {
    if (!task?.id) return;
    try {
      setIsUpdating(true);
      setUploadError('');
      setUploadSuccess('');
      const response = await dataAPI.startTask(task.id);
      if (response.status === 'success') {
        setUploadSuccess(response.message || 'Task started successfully.');
        setTimeout(() => setUploadSuccess(''), 3000);
        await fetchTaskDetails(false);
      } else {
        const errorMsg = response.message || 'Failed to start task. Please try again.';
        setUploadError(errorMsg);
        setTimeout(() => setUploadError(''), 6000);
      }
    } catch (error) {
      console.error('Error starting task:', error);
      const errorMsg = error?.message || 'Failed to start task. Please try again.';
      setUploadError(errorMsg);
      setTimeout(() => setUploadError(''), 6000);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle end task - POST company/tasks/end/:taskId (ends timelog session)
  const handleEndTask = async () => {
    if (!task?.id) return;
    try {
      setIsUpdating(true);
      setUploadError('');
      setUploadSuccess('');
      const response = await dataAPI.endTask(task.id);
      if (response.status === 'success') {
        setUploadSuccess(response.message || 'Task session ended successfully.');
        setTimeout(() => setUploadSuccess(''), 3000);
        await fetchTaskDetails(false);
      } else {
        const errorMsg = response.message || 'Failed to end task. Please try again.';
        setUploadError(errorMsg);
        setTimeout(() => setUploadError(''), 6000);
      }
    } catch (error) {
      console.error('Error ending task:', error);
      const errorMsg = error?.message || 'Failed to end task. Please try again.';
      setUploadError(errorMsg);
      setTimeout(() => setUploadError(''), 6000);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle accept task - change status from "To Do" to "In Progress"
  const handleAcceptTask = async () => {
    try {
      setIsUpdating(true);
      setUploadError('');
      setUploadSuccess('');

      // Update task status to "In Progress" when accepting
      const response = await dataAPI.updateTaskStatus(task.id, 'In Progress');

      if (response.status === 'success') {
        // Update local task state; use response.started_at if API returns it, else use now for elapsed display
        const startedAt = response.data?.started_at || new Date().toISOString();
        setTask(prev => ({
          ...prev,
          status: 'In Progress',
          started_at: response.data?.started_at || prev.started_at || startedAt,
          updated_at: response.data?.updated_at || startedAt
        }));
        setUploadSuccess('Task accepted successfully!');
        setTimeout(() => {
          setUploadSuccess('');
        }, 3000);
      } else {
        setUploadError('Failed to accept task. Please try again.');
        setTimeout(() => {
          setUploadError('');
        }, 5000);
      }
    } catch (error) {
      console.error('Error accepting task:', error);
      setUploadError('Failed to accept task. Please try again.');
      setTimeout(() => {
        setUploadError('');
      }, 5000);
    } finally {
      setIsUpdating(false);
    }
  };

  // Open time logs modal and fetch timelogs for the task
  const handleOpenTimelogModal = async () => {
    if (!task?.id) return;
    setTimelogModalOpen(true);
    setTimelogsLoading(true);
    setTimelogs([]);
    try {
      const response = await dataAPI.getTaskTimelogs(task.id);
      if (response.status === 'success' && response.data?.timelogs) {
        setTimelogs(response.data.timelogs);
      } else {
        setTimelogs([]);
      }
    } catch (error) {
      console.error('Error fetching timelogs:', error);
      setTimelogs([]);
    } finally {
      setTimelogsLoading(false);
    }
  };

  const handleStartEditTimelog = (log, fallbackId) => {
    const id = log.id || fallbackId;
    setTimelogEditError('');
    setEditingTimelog({
      id,
      startInput: toLocalDateTimeInput(log.start_time),
      endInput: toLocalDateTimeInput(log.end_time),
    });
  };

  const handleCancelEditTimelog = () => {
    setEditingTimelog(null);
    setTimelogEditError('');
  };

  const handleSaveTimelogEdit = async () => {
    if (!editingTimelog || !task?.id) return;

    const startIso = fromLocalDateTimeInput(editingTimelog.startInput);
    const endIso = fromLocalDateTimeInput(editingTimelog.endInput);

    if (!startIso) {
      setTimelogEditError('Please provide a valid start time.');
      return;
    }

     // Validation: do not allow future dates/times
    const now = new Date();
    const startDateObj = new Date(startIso);
    const endDateObj = endIso ? new Date(endIso) : null;

    if (startDateObj > now) {
      setTimelogEditError('Start time cannot be in the future.');
      return;
    }

    if (endDateObj && endDateObj > now) {
      setTimelogEditError('End time cannot be in the future.');
      return;
    }

    // Optional: ensure end is not before start when provided
    if (endDateObj && endDateObj < startDateObj) {
      setTimelogEditError('End time cannot be before start time.');
      return;
    }

    try {
      setIsSavingTimelog(true);
      setTimelogEditError('');

      const payload = {
        start_time: startIso,
        end_time: endIso,
      };

      const response = await dataAPI.updateTaskTimelog(editingTimelog.id, payload);

      if (response.status === 'success') {
        // Refresh timelogs list after successful update
        try {
          const refreshResponse = await dataAPI.getTaskTimelogs(task.id);
          if (refreshResponse.status === 'success' && refreshResponse.data?.timelogs) {
            setTimelogs(refreshResponse.data.timelogs);
          }
        } catch (innerError) {
          console.error('Error refreshing timelogs after update:', innerError);
        }

        setEditingTimelog(null);
        setTimelogEdited(true);
      } else {
        setTimelogEditError(response.message || 'Failed to update time log. Please try again.');
      }
    } catch (error) {
      console.error('Error updating timelog:', error);
      setTimelogEditError(error?.message || 'Failed to update time log. Please try again.');
    } finally {
      setIsSavingTimelog(false);
    }
  };

  const handleCloseTimelogModal = async () => {
    setTimelogModalOpen(false);
    setTimelogs([]);
    setEditingTimelog(null);
    setTimelogEditError('');

    // If any timelog was edited during this session, refresh task details
    if (timelogEdited) {
      try {
        await fetchTaskDetails(false);
      } catch (error) {
        console.error('Error refreshing task details after timelog edit:', error);
      } finally {
        setTimelogEdited(false);
      }
    }
  };

  // Helper function to get priority color
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return '#f44336'; // Red
      case 'high':
        return '#f44336'; // Red
      case 'medium':
        return '#ff9800'; // Orange
      case 'low':
        return '#4caf50'; // Green
      default:
        return '#666666'; // Default gray
    }
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'to do':
      case 'todo':
        return '#6C757D'; // Medium gray background for better contrast with white text
      case 'in progress':
      case 'inprogress':
        return '#26C3C0'; // Blue background for In Progress
      case 'done':
      case 'completed':
        return '#4caf50'; // Green background for Completed
      case 'review':
      case 'in review':
        return '#ff9800'; // Orange background for Review
      case 'blocked':
        return '#f44336'; // Red background for Blocked
      default:
        return '#2a2a2a'; // Dark gray background like kanban chips
    }
  };

  // Helper: convert total_duration_minutes from API to "Xh Ym" (e.g. 90 → "1h 30m"); accepts decimals
  const formatDurationMinutes = (minutes) => {
    if (minutes == null || minutes === '' || isNaN(Number(minutes))) return '—';
    const m = Math.floor(Number(minutes));
    if (m < 0) return '0m';
    const hours = Math.floor(m / 60);
    const mins = m % 60;
    if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
    if (hours > 0) return `${hours}h`;
    if (mins > 0) return `${mins}m`;
    return '0m';
  };

  // Helper: format elapsed time as HH:MM:SS (e.g. 1:05:30, 00:00:45) from minutes (supports decimals for live timer)
  const formatElapsedHms = (minutes) => {
    if (minutes == null || minutes === '' || isNaN(Number(minutes)) || Number(minutes) < 0) return '0:00:00';
    const totalSeconds = Math.floor(Number(minutes) * 60);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return `${h}:${pad(m)}:${pad(s)}`;
  };

  // Sync live timer base from API: when session is active, base = total_duration_minutes (or 0 if null)
  useEffect(() => {
    if (!task?.is_session_active) return;
    const baseMins = task.total_duration_minutes ?? task.total_duration_minut ?? 0;
    setSessionBaseMinutes(typeof baseMins === 'number' ? baseMins : Number(baseMins) || 0);
    setSessionBaseTime(Date.now());
  }, [task?.id, task?.is_session_active, task?.total_duration_minutes, task?.total_duration_minut]);

  // When session is active, tick every second so "Elapsed time" counts up live
  useEffect(() => {
    if (!task?.is_session_active) return;
    const interval = setInterval(() => setLiveTick(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [task?.id, task?.is_session_active]);

  // When session is active, refetch task every minute so total_duration_minutes stays in sync
  useEffect(() => {
    if (!task?.id || !task?.is_session_active) return;
    const interval = setInterval(() => fetchTaskDetails(false), 60000);
    return () => clearInterval(interval);
  }, [task?.id, task?.is_session_active]);

  // Helper function to format datetime consistently
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'No date';

    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return 'Invalid date';

      // Format: "9:30 PM, 26 Aug 2025"
      const time = date.toLocaleString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });

      const datePart = date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });

      return `${time}, ${datePart}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  // Convert an API datetime string to a value suitable for <input type="datetime-local">
  const toLocalDateTimeInput = (dateTimeString) => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Convert a <input type="datetime-local"> value back to ISO string for API
  const fromLocalDateTimeInput = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (isNaN(date.getTime())) return null;
    return date.toISOString();
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return <img
          src={PDFIcon}
          alt="PDF"
          style={{
            width: '24px',
            height: '24px'
          }}
        />;
      case 'doc':
      case 'docx':
        return <InsertDriveFileIcon style={{ fontSize: '24px', color: '#2196f3' }} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
        return <ImageIcon style={{ fontSize: '24px', color: '#4caf50' }} />;
      default:
        return <InsertDriveFileIcon style={{ fontSize: '24px', color: '#666' }} />;
    }
  };

  if (isLoading) {
    return (
      <div style={{
        padding: '24px',
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          fontSize: '18px',
          color: mode === 'dark' ? '#FFFFFF' : '#222222',
          fontFamily: "'Urbanist', Arial, sans-serif"
        }}>
          Loading task details...
        </div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div style={{
        padding: '24px',
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '18px',
          color: '#f44336',
          marginBottom: '16px',
          fontFamily: "'Urbanist', Arial, sans-serif"
        }}>
          {error || 'Task not found'}
        </div>
        <button
          onClick={handleBack}
          style={{
            background: '#26C3C0',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer',
            fontFamily: "'Urbanist', Arial, sans-serif",
            transition: 'background-color 0.2s ease'
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  // Whether current user can edit time logs in the modal:
  // treat "project owner / assigner" the same way as other creator checks in this file.
  // This allows edit when the logged-in user is the one who assigned/created the task,
  // even if they are also the assignee.
  const canCurrentUserEditTimelogs =
    !!(currentUserId && task && (
      task.created_by_user_id === currentUserId ||
      String(task.created_by) === String(currentUserId) ||
      String(task.assigned_by) === String(currentUserId)
    ));

  return (
    <>
    <div
      className="task-details-page"
      style={{
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
        minHeight: '100vh',
        padding: '24px',
        overflowX: 'hidden',
        maxWidth: '100vw',
        boxSizing: 'border-box',
        width: '100%'
      }}
    >
      {/* Add CSS animation for loading spinner */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          /* Hide scrollbar but keep scrolling functionality */
          .description-scroll::-webkit-scrollbar {
            width: 0;
            display: none;
          }
          
          .description-scroll::-webkit-scrollbar-track {
            display: none;
          }
          
          .description-scroll::-webkit-scrollbar-thumb {
            display: none;
          }
          
          /* Firefox scrollbar - hide completely */
          .description-scroll {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
        `}
      </style>

      {/* Header */}
      <div
        className="task-details-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          marginBottom: '24px',
          width: '100%'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
          <button
            onClick={handleBack}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '50%',
              color: mode === 'dark' ? '#FFFFFF' : '#222222',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = mode === 'dark' ? '#333' : '#f0f0f0';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            <ArrowBackIcon style={{ fontSize: '24px' }} />
          </button>

          <div style={{ marginLeft: '6px' }}>
            <h1 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#26C3C0',
              margin: '0',
              fontFamily: "'Urbanist', Arial, sans-serif",
              lineHeight: '1.2'
            }}>
              Tasks
            </h1>
          </div>
        </div>

      </div>

      <div
        className={`task-details-content task-content ${isUpdating ? 'updating' : ''}`}
        style={{
          display: 'flex',
          gap: '24px',
          alignItems: 'stretch',
          transition: 'opacity 0.2s ease',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          minWidth: 0
        }}
      >
        <style>
          {`
              @keyframes fadeIn {
                from { opacity: 0; transform: translateY(10px); }
                to { opacity: 1; transform: translateY(0); }
              }
              .task-content {
                animation: fadeIn 0.3s ease-out;
              }
                             .task-content.updating {
                opacity: 0.8;
              }
              
              /* ReactQuill custom styles */
              .ql-editor {
                background-color: ${mode === 'dark' ? '#2a2a2a' : '#ffffff'} !important;
                color: ${mode === 'dark' ? '#ffffff' : '#000000'} !important;
                font-family: 'Urbanist', Arial, sans-serif !important;
                font-size: 14px !important;
                line-height: 1.6 !important;
                min-height: 100px !important;
                max-height: 120px !important;
                overflow-y: auto !important;
              }
              .ql-editor * {
                font-family: 'Urbanist', Arial, sans-serif !important;
              }
              .ql-editor p, .ql-editor div, .ql-editor span, .ql-editor strong, .ql-editor em, .ql-editor ul, .ql-editor ol, .ql-editor li {
                font-family: 'Urbanist', Arial, sans-serif !important;
                font-size: 14px !important;
              }
              /* More aggressive font overrides for ReactQuill */
              .ql-editor[contenteditable="true"] {
                font-family: 'Urbanist', Arial, sans-serif !important;
              }
              .ql-editor[contenteditable="true"] * {
                font-family: 'Urbanist', Arial, sans-serif !important;
              }
              .ql-editor.ql-blank {
                font-family: 'Urbanist', Arial, sans-serif !important;
              }
              .ql-editor.ql-blank::before {
                font-family: 'Urbanist', Arial, sans-serif !important;
              }
              .ql-editor::-webkit-scrollbar {
                width: 6px !important;
              }
              .ql-editor::-webkit-scrollbar-track {
                background: ${mode === 'dark' ? '#1a1a1a' : '#f1f1f1'} !important;
                border-radius: 3px !important;
              }
              .ql-editor::-webkit-scrollbar-thumb {
                background: ${mode === 'dark' ? '#555' : '#c1c1c1'} !important;
                border-radius: 3px !important;
              }
              .ql-editor::-webkit-scrollbar-thumb:hover {
                background: ${mode === 'dark' ? '#777' : '#a1a1a1'} !important;
              }
              .ql-toolbar {
                background-color: ${mode === 'dark' ? '#333' : '#f8f9fa'} !important;
                border-color: ${mode === 'dark' ? '#444' : '#ddd'} !important;
                border-radius: 8px 8px 0 0 !important;
                padding: 8px 12px !important;
              }
              .ql-container {
                border-color: ${mode === 'dark' ? '#444' : '#ddd'} !important;
                border-radius: 0 0 8px 8px !important;
              }
              .ql-toolbar .ql-stroke {
                stroke: ${mode === 'dark' ? '#ffffff' : '#444'} !important;
              }
              .ql-toolbar .ql-fill {
                fill: ${mode === 'dark' ? '#ffffff' : '#444'} !important;
              }
              .ql-toolbar button {
                border: 1px solid ${mode === 'dark' ? '#555' : '#ccc'} !important;
                border-radius: 6px !important;
                background-color: ${mode === 'dark' ? '#444' : '#ffffff'} !important;
                margin: 0 2px !important;
                padding: 6px 8px !important;
                height: 32px !important;
                min-width: 32px !important;
                transition: all 0.2s ease !important;
                box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
              }
              .ql-toolbar button:hover {
                background-color: ${mode === 'dark' ? '#555' : '#f0f0f0'} !important;
                border-color: #26C3C0 !important;
                box-shadow: 0 2px 6px rgba(38, 195, 192, 0.2) !important;
              }
              .ql-toolbar button.ql-active {
                background-color: #26C3C0 !important;
                border-color: #26C3C0 !important;
                color: white !important;
                box-shadow: 0 2px 8px rgba(38, 195, 192, 0.3) !important;
              }
              .ql-toolbar button.ql-active .ql-stroke {
                stroke: white !important;
              }
                          .ql-toolbar button.ql-active .ql-fill {
              fill: white !important;
            }
            
            /* Fix line spacing in description view */
            .description-scroll p {
              margin: 0 0 2px 0 !important;
              padding: 0 !important;
            }
            .description-scroll ul, .description-scroll ol {
              margin: 0 0 2px 0 !important;
              padding-left: 20px !important;
            }
            .description-scroll li {
              margin: 0 0 1px 0 !important;
              padding: 0 !important;
            }
            .description-scroll p:last-child, .description-scroll ul:last-child, .description-scroll ol:last-child {
              margin-bottom: 0 !important;
            }
            .description-scroll br {
              margin: 0 !important;
              line-height: 1.2 !important;
            }
            /* Target consecutive paragraphs to reduce gaps */
            .description-scroll p + p {
              margin-top: 0 !important;
            }
            .description-scroll p + ul, .description-scroll p + ol {
              margin-top: 0 !important;
            }
            .description-scroll ul + p, .description-scroll ol + p {
              margin-top: 0 !important;
            }
            
            /* ReactQuill placeholder styling */
            .ql-editor.ql-blank::before {
              color: ${mode === 'dark' ? '#B0B0B0' : '#999999'} !important;
              font-style: normal !important;
              font-family: 'Urbanist', Arial, sans-serif !important;
              font-size: 14px !important;
            }
          `}
        </style>
        {/* Left Side - Main Components (65% width) */}
        <div
          className="task-details-left"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            flex: '0 0 calc(65% - 12px)',
            width: 'calc(65% - 12px)',
            maxWidth: 'calc(65% - 12px)',
            minWidth: 0,
            overflowX: 'hidden',
            overflowY: 'visible',
            boxSizing: 'border-box'
          }}
        >
          {/* Task Details */}
          <div style={{
            backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
            borderRadius: '12px',
            padding: '24px',
            border: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'hidden'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <span style={{
                fontSize: '18px',
                fontWeight: '600',
                color: mode === 'dark' ? '#FFFFFF' : '#222222',
                fontFamily: "'Urbanist', Arial, sans-serif",
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                {editTaskDetails ? (
                  <TextField
                    size="small"
                    required
                    label="Task Title"
                    value={editData.title || ''}
                    onChange={(e) => handleEditDataChange('title', e.target.value)}
                    disabled={isLoadingEnums || isUpdating}
                    error={!editData.title || editData.title.trim() === ''}
                    helperText={(!editData.title || editData.title.trim() === '') ? 'Title is required' : ''}
                    style={{
                      backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
                      color: mode === 'dark' ? '#FFFFFF' : '#222222',
                      minWidth: '300px'
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease',
                        '& fieldset': {
                          borderColor: mode === 'dark' ? '#444' : '#ddd',
                        },
                        '&:hover fieldset': {
                          borderColor: '#26C3C0',
                          boxShadow: '0 4px 8px rgba(38,195,192,0.2)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#26C3C0',
                          boxShadow: '0 0 0 2px rgba(38,195,192,0.1)',
                        },
                        '&.Mui-error fieldset': {
                          borderColor: '#f44336',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: mode === 'dark' ? '#999' : '#666',
                        fontSize: '14px',
                      },
                      '& .MuiFormHelperText-root': {
                        color: '#f44336',
                        fontSize: '12px',
                      },
                    }}
                  />
                ) : (
                  <>
                    {task.title}
                    {isUpdating && (
                      <div style={{
                        width: '14px',
                        height: '14px',
                        border: '2px solid #26C3C0',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                    )}
                  </>
                )}
              </span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {/* Accept Button - Show only if task is "To Do" and assigned to current user */}
                {(() => {
                  if (!task) return null;

                  const taskStatus = (task.status || '').trim().toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ');
                  const normalizedStatus = taskStatus.replace(/\s+/g, ' ').trim();
                  const isTodoStatus = normalizedStatus === 'to do' ||
                    normalizedStatus === 'todo' ||
                    task.status === 'To Do' ||
                    task.status === 'to do' ||
                    task.status === 'todo' ||
                    task.status === 'to_do';

                  const assignedToId = task.assigned_to ||
                    task.assigned_to_user_id ||
                    task.assigned_user_id ||
                    task.assigned_to_id ||
                    task.assigned_user?.id;

                  const isAssignedToCurrentUser = currentUserId && assignedToId &&
                    (String(assignedToId) === String(currentUserId) ||
                      Number(assignedToId) === Number(currentUserId));

                  const showAcceptButton = isTodoStatus && isAssignedToCurrentUser;

                  return showAcceptButton ? (
                    <button
                      onClick={handleAcceptTask}
                      disabled={isUpdating}
                      style={{
                        background: '#26C3C0',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: isUpdating ? 'not-allowed' : 'pointer',
                        fontFamily: "'Urbanist', Arial, sans-serif",
                        transition: 'all 0.2s ease',
                        opacity: isUpdating ? 0.6 : 1,
                        boxShadow: '0 2px 4px rgba(38, 195, 192, 0.2)',
                      }}
                      onMouseEnter={(e) => {
                        if (!isUpdating) {
                          e.currentTarget.style.background = '#22B0AD';
                          e.currentTarget.style.boxShadow = '0 2px 6px rgba(38, 195, 192, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isUpdating) {
                          e.currentTarget.style.background = '#26C3C0';
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(38, 195, 192, 0.2)';
                        }
                      }}
                    >
                      Accept
                    </button>
                  ) : null;
                })()}
                {editTaskDetails ? (
                  <>
                    <button
                      onClick={() => handleSave('taskDetails')}
                      disabled={isUpdating}
                      style={{
                        background: isUpdating ? '#999' : '#26C3C0',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        color: '#FFFFFF',
                        cursor: isUpdating ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '14px',
                        opacity: isUpdating ? 0.7 : 1,
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {isUpdating ? (
                        <>
                          <div style={{
                            width: '14px',
                            height: '14px',
                            border: '2px solid #ffffff',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }} />
                          Updating...
                        </>
                      ) : (
                        <>
                          <SaveIcon style={{ fontSize: '16px' }} />
                          Save
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleCancelEdit('taskDetails')}
                      style={{
                        background: 'none',
                        border: '1px solid #666',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        color: '#666',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '14px'
                      }}
                    >
                      <CloseIcon style={{ fontSize: '16px' }} />
                      Cancel
                    </button>
                  </>
                ) : (
                  canUserEditTask() && (
                    <button
                      onClick={() => toggleEditMode('taskDetails')}
                      style={{
                        background: 'none',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px',
                        color: '#26C3C0',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <EditIcon style={{ fontSize: '18px' }} />
                    </button>
                  )
                )}
              </div>
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {/* Assigned By */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '120px'
                }}>
                  <img
                    src={UserIcon}
                    alt="Assigned By"
                    style={{
                      width: '18px',
                      height: '18px',
                      filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(40%)',
                      opacity: 0.7
                    }}
                  />
                  <span style={{
                    fontSize: '14px',
                    color: mode === 'dark' ? '#999' : '#666',
                    fontFamily: "'Urbanist', Arial, sans-serif",
                    fontWeight: '500'
                  }}>
                    Assigned By
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <span style={{
                    padding: '8px 8px',
                    backgroundColor: 'transparent',
                    color: mode === 'dark' ? '#FFFFFF' : '#222222',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: "'Urbanist', Arial, sans-serif",
                    border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {task.created_by_profile_pic ? (
                      <img
                        src={getProfilePicUrl(task.created_by_profile_pic)}
                        alt="Profile"
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          // Replace the image with a fallback avatar when it fails to load
                          const fallbackDiv = document.createElement('div');
                          fallbackDiv.style.cssText = `
                            width: 20px;
                            height: 20px;
                            border-radius: 50%;
                            background-color: #26C3C0;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            color: #FFFFFF;
                            font-size: 12px;
                            font-weight: 600;
                          `;
                          fallbackDiv.textContent = task.created_by_username ? task.created_by_username.charAt(0).toUpperCase() : 'U';
                          e.target.parentNode.replaceChild(fallbackDiv, e.target);
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: '#26C3C0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#FFFFFF',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {task.created_by_username ? task.created_by_username.charAt(0).toUpperCase() : 'U'}
                      </div>
                    )}
                    {task?.created_by_user_details?.full_name || 'Unknown'}
                  </span>
                </div>
              </div>

              {/* Assigned To - Single User Only */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '120px'
                }}>
                  <img
                    src={Users2Icon}
                    alt="Assigned To"
                    style={{
                      width: '18px',
                      height: '18px',
                      filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(40%)',
                      opacity: 0.7
                    }}
                  />
                  <span style={{
                    fontSize: '14px',
                    color: mode === 'dark' ? '#999' : '#666',
                    fontFamily: "'Urbanist', Arial, sans-serif",
                    fontWeight: '500'
                  }}>
                    Assigned to
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {editTaskDetails ? (
                    <FormControl size="small" style={{ minWidth: '200px' }}>
                      <Select
                        value={editData.assigned_to || ''}
                        onChange={(e) => handleEditDataChange('assigned_to', e.target.value)}
                        disabled={isLoadingEmployees}
                        style={{
                          backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
                          color: mode === 'dark' ? '#FFFFFF' : '#222222'
                        }}
                      >
                        {isLoadingEmployees ? (
                          <MenuItem disabled>Loading employees...</MenuItem>
                        ) : (
                          employees.map((employee) => (
                            <MenuItem key={employee.id} value={employee.id}>
                              {employee.full_name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  ) : (
                    <span style={{
                      padding: '8px 8px',
                      backgroundColor: 'transparent',
                      color: mode === 'dark' ? '#FFFFFF' : '#222222',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      fontFamily: "'Urbanist', Arial, sans-serif",
                      border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {task.assigned_user_details?.profile_pic ? (
                        <img
                          src={getProfilePicUrl(task.assigned_user_details.profile_pic)}
                          alt="Profile"
                          style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            // Replace the image with a fallback avatar when it fails to load
                            const fallbackDiv = document.createElement('div');
                            fallbackDiv.style.cssText = `
                             width: 20px;
                             height: 20px;
                             border-radius: 50%;
                             background-color: #26C3C0;
                             display: flex;
                             align-items: center;
                             justify-content: center;
                             color: #FFFFFF;
                             font-size: 12px;
                             font-weight: 600;
                           `;
                            fallbackDiv.textContent = task.assigned_user_details?.full_name ? task.assigned_user_details.full_name.charAt(0).toUpperCase() : 'U';
                            e.target.parentNode.replaceChild(fallbackDiv, e.target);
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#26C3C0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#FFFFFF',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {task.assigned_user_details?.full_name ? task.assigned_user_details.full_name.charAt(0).toUpperCase() : 'U'}
                        </div>
                      )}
                      {task.assigned_user_details?.full_name || 'Unassigned'}
                    </span>
                  )}
                </div>
              </div>

              {/* Timeline */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '120px'
                }}>
                  <img
                    src={CalendarIcon}
                    alt="Calendar"
                    style={{
                      width: '18px',
                      height: '18px',
                      filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(40%)',
                      opacity: 0.7
                    }}
                  />
                  <span style={{
                    fontSize: '14px',
                    color: mode === 'dark' ? '#999' : '#666',
                    fontFamily: "'Urbanist', Arial, sans-serif",
                    fontWeight: '500'
                  }}>
                    Timeline
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {editTaskDetails ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      maxWidth: '320px'
                    }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                        <DatePicker
                          label="Start Date"
                          value={editData.start_date ? new Date(editData.start_date) : null}
                          onChange={(newValue) => {
                            const formattedDate = formatDateToAPI(newValue);
                            handleEditDataChange('start_date', formattedDate);
                          }}
                          format="dd-MM-yyyy"
                          disabled={isLoadingEnums}
                          minDate={undefined}
                          slotProps={{
                            textField: {
                              size: "small",
                              style: {
                                width: '150px',
                                maxWidth: '150px',
                                backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
                                color: mode === 'dark' ? '#FFFFFF' : '#222222',
                                fontSize: '11px'
                              },
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                  backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
                                  color: mode === 'dark' ? '#FFFFFF' : '#222222',
                                  fontSize: '11px !important',
                                  fontFamily: "'Urbanist', Arial, sans-serif",
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  transition: 'all 0.2s ease',
                                  height: '34px',
                                  '& fieldset': {
                                    borderColor: mode === 'dark' ? '#444' : '#ddd',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: '#26C3C0',
                                    boxShadow: '0 4px 8px rgba(38,195,192,0.2)',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#26C3C0',
                                    boxShadow: '0 0 0 2px rgba(38,195,192,0.1)',
                                  },
                                },
                                '& .MuiInputBase-input': {
                                  padding: '4px 6px !important',
                                  color: mode === 'dark' ? '#FFFFFF !important' : '#222222 !important',
                                  fontSize: '11px !important',
                                  height: '28px',
                                  lineHeight: '1.3',
                                  fontFamily: 'Urbanist, Arial, sans-serif !important',
                                },
                                '& input': {
                                  fontSize: '11px !important',
                                  fontFamily: 'Urbanist, Arial, sans-serif !important',
                                  color: mode === 'dark' ? '#FFFFFF !important' : '#222222 !important',
                                  padding: '4px 6px !important',
                                },
                                '& .MuiInputAdornment-root': {
                                  marginLeft: '4px !important',
                                  marginRight: '2px !important',
                                },
                                '& .MuiInputLabel-root': {
                                  fontSize: '11px',
                                  color: mode === 'dark' ? '#999' : '#666',
                                  transform: 'translate(10px, 7px) scale(1)',
                                  '&.Mui-focused, &.MuiFormLabel-filled': {
                                    transform: 'translate(10px, -7px) scale(0.8)',
                                  },
                                },
                              },
                            },
                          }}
                        />
                        <span style={{ color: mode === 'dark' ? '#999' : '#666', margin: '0 4px' }}>-</span>
                        <DatePicker
                          label="Due Date"
                          value={editData.due_date ? new Date(editData.due_date) : null}
                          onChange={(newValue) => {
                            const formattedDate = formatDateToAPI(newValue);
                            handleEditDataChange('due_date', formattedDate);
                          }}
                          format="dd-MM-yyyy"
                          disabled={isLoadingEnums}
                          minDate={undefined}
                          slotProps={{
                            textField: {
                              size: "small",
                              style: {
                                width: '150px',
                                maxWidth: '150px',
                                backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
                                color: mode === 'dark' ? '#FFFFFF' : '#222222',
                                fontSize: '11px'
                              },
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                  backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
                                  color: mode === 'dark' ? '#FFFFFF' : '#222222',
                                  fontSize: '11px !important',
                                  fontFamily: "'Urbanist', Arial, sans-serif",
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  transition: 'all 0.2s ease',
                                  height: '34px',
                                  '& fieldset': {
                                    borderColor: mode === 'dark' ? '#444' : '#ddd',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: '#26C3C0',
                                    boxShadow: '0 4px 8px rgba(38,195,192,0.2)',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#26C3C0',
                                    boxShadow: '0 0 0 2px rgba(38,195,192,0.1)',
                                  },
                                },
                                '& .MuiInputBase-input': {
                                  padding: '4px 6px !important',
                                  color: mode === 'dark' ? '#FFFFFF !important' : '#222222 !important',
                                  fontSize: '11px !important',
                                  height: '28px',
                                  lineHeight: '1.3',
                                  fontFamily: 'Urbanist, Arial, sans-serif !important',
                                },
                                '& input': {
                                  fontSize: '11px !important',
                                  fontFamily: 'Urbanist, Arial, sans-serif !important',
                                  color: mode === 'dark' ? '#FFFFFF !important' : '#222222 !important',
                                  padding: '4px 6px !important',
                                },
                                '& .MuiInputAdornment-root': {
                                  marginLeft: '4px !important',
                                  marginRight: '2px !important',
                                },
                                '& .MuiInputLabel-root': {
                                  fontSize: '11px',
                                  color: mode === 'dark' ? '#999' : '#666',
                                  transform: 'translate(10px, 7px) scale(1)',
                                  '&.Mui-focused, &.MuiFormLabel-filled': {
                                    transform: 'translate(10px, -7px) scale(0.8)',
                                  },
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </div>
                  ) : (
                    <span style={{
                      padding: '8px 16px',
                      backgroundColor: 'transparent',
                      color: mode === 'dark' ? '#FFFFFF' : '#222222',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      fontFamily: "'Urbanist', Arial, sans-serif",
                      border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`
                    }}>
                      {task.start_date && task.due_date ?
                        `${formatDate(task.start_date) || 'Not set'} - ${formatDate(task.due_date) || 'Not set'}` :
                        task.due_date ?
                          `Not set - ${formatDate(task.due_date) || 'Not set'}` :
                          'Timeline not set'
                      }
                    </span>
                  )}
                </div>
              </div>

              {/* Start Date */}
              {projectDetails?.start_date && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    minWidth: '120px'
                  }}>
                    <img
                      src={CalendarIcon}
                      alt="Calendar"
                      style={{
                        width: '18px',
                        height: '18px',
                        filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(40%)',
                        opacity: 0.7
                      }}
                    />
                    <span style={{
                      fontSize: '14px',
                      color: mode === 'dark' ? '#999' : '#666',
                      fontFamily: "'Urbanist', Arial, sans-serif",
                      fontWeight: '500'
                    }}>
                      Start Date
                    </span>
                  </div>
                  <span style={{
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    color: mode === 'dark' ? '#FFFFFF' : '#222222',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: "'Urbanist', Arial, sans-serif",
                    border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`
                  }}>
                    {formatDate(projectDetails.start_date) || 'Not set'}
                  </span>
                </div>
              )}

              {/* End Date */}
              {projectDetails?.end_date && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    minWidth: '120px'
                  }}>
                    <img
                      src={CalendarIcon}
                      alt="Calendar"
                      style={{
                        width: '18px',
                        height: '18px',
                        filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(40%)',
                        opacity: 0.7
                      }}
                    />
                    <span style={{
                      fontSize: '14px',
                      color: mode === 'dark' ? '#999' : '#666',
                      fontFamily: "'Urbanist', Arial, sans-serif",
                      fontWeight: '500'
                    }}>
                      End Date
                    </span>
                  </div>
                  <span style={{
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    color: mode === 'dark' ? '#FFFFFF' : '#222222',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: "'Urbanist', Arial, sans-serif",
                    border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`
                  }}>
                    {formatDate(projectDetails.end_date) || 'Not set'}
                  </span>
                </div>
              )}

              {/* Priority */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '120px'
                }}>
                  <img src={icPriority} alt="Priority" style={{ width: '18px', height: '18px', color: mode === 'dark' ? '#999' : '#666' }} />
                  <span style={{
                    fontSize: '14px',
                    color: mode === 'dark' ? '#999' : '#666',
                    fontFamily: "'Urbanist', Arial, sans-serif",
                    fontWeight: '500'
                  }}>
                    Priority
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {editTaskDetails ? (
                    <FormControl size="small" style={{ minWidth: '200px' }}>
                      <Select
                        value={editData.priority || ''}
                        onChange={(e) => handleEditDataChange('priority', e.target.value)}
                        disabled={isLoadingEnums}
                        style={{
                          backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
                          color: mode === 'dark' ? '#FFFFFF' : '#222222'
                        }}
                      >
                        {isLoadingEnums ? (
                          <MenuItem disabled>Loading priorities...</MenuItem>
                        ) : (
                          priorityOptions.map((priority) => {
                            console.log('Rendering priority option:', priority);
                            return (
                              <MenuItem key={priority.value} value={priority.value}>
                                {priority.label}
                              </MenuItem>
                            );
                          })
                        )}
                      </Select>
                    </FormControl>
                  ) : (
                    <span style={{
                      padding: '8px 16px',
                      backgroundColor: getPriorityColor(task.priority),
                      color: '#FFFFFF',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      fontFamily: "'Urbanist', Arial, sans-serif",
                      textTransform: 'capitalize',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      border: 'none'
                    }}>
                      {task.priority || 'Medium'}
                    </span>
                  )}
                </div>
              </div>

              {/* Status */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '120px'
                }}>
                  <img src={status_cat} alt="Status" style={{ width: '18px', height: '18px', color: mode === 'dark' ? '#999' : '#666' }} />
                  <span style={{
                    fontSize: '14px',
                    color: mode === 'dark' ? '#999' : '#666',
                    fontFamily: "'Urbanist', Arial, sans-serif",
                    fontWeight: '500'
                  }}>
                    Status
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {editTaskDetails ? (
                    <FormControl size="small" style={{ width: 'auto', minWidth: 'fit-content' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Select
                          value={task.status || ''}
                          onChange={handleStatusChange}
                          disabled={isLoadingEnums || !canUserChangeStatus() || isUpdating}
                          style={{
                            backgroundColor: getStatusColor(task.status),
                            color: '#FFFFFF !important',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '500',
                            fontFamily: "'Urbanist', Arial, sans-serif",
                            width: 'fit-content',
                            cursor: canUserChangeStatus() && !isUpdating ? 'pointer' : 'not-allowed',
                            opacity: isUpdating ? 0.7 : 1,
                            transition: 'opacity 0.2s ease'
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                border: 'none'
                              },
                              '&:hover fieldset': {
                                border: 'none'
                              },
                              '&.Mui-focused fieldset': {
                                border: 'none'
                              },
                              '&:hover': {
                                opacity: 0.9,
                                transform: 'translateY(-1px)',
                                transition: 'all 0.2s ease'
                              },
                              color: '#FFFFFF !important'
                            },
                            '& .MuiSelect-icon': {
                              color: '#FFFFFF !important'
                            },
                            '& .MuiSelect-select': {
                              padding: '8px 16px',
                              textAlign: 'center',
                              color: '#FFFFFF !important'
                            },
                            '& .MuiInputBase-input': {
                              color: '#FFFFFF !important'
                            },
                            '& .MuiInputBase-root': {
                              color: '#FFFFFF !important'
                            },
                            '&.Mui-disabled': {
                              backgroundColor: getStatusColor(task.status) + ' !important',
                              color: '#FFFFFF !important',
                              opacity: '1 !important',
                              '& .MuiSelect-select': {
                                color: '#FFFFFF !important'
                              },
                              '& .MuiSelect-icon': {
                                color: '#FFFFFF !important'
                              },
                              '& .MuiInputBase-input': {
                                color: '#FFFFFF !important'
                              }
                            },
                            '& .Mui-disabled': {
                              backgroundColor: getStatusColor(task.status) + ' !important',
                              color: '#FFFFFF !important',
                              opacity: '1 !important',
                              '& .MuiSelect-select': {
                                color: '#FFFFFF !important'
                              },
                              '& .MuiSelect-icon': {
                                color: '#FFFFFF !important'
                              },
                              '& .MuiInputBase-input': {
                                color: '#FFFFFF !important'
                              }
                            },
                            '& .MuiInputBase-root.Mui-disabled': {
                              backgroundColor: getStatusColor(task.status) + ' !important',
                              color: '#FFFFFF !important',
                              opacity: '1 !important',
                              '& .MuiSelect-select': {
                                color: '#FFFFFF !important'
                              },
                              '& .MuiSelect-icon': {
                                color: '#FFFFFF !important'
                              },
                              '& .MuiInputBase-input': {
                                color: '#FFFFFF !important'
                              }
                            },
                            '& .MuiOutlinedInput-root.Mui-disabled': {
                              backgroundColor: getStatusColor(task.status) + ' !important',
                              color: '#FFFFFF !important',
                              opacity: '1 !important',
                              '& .MuiSelect-select': {
                                color: '#FFFFFF !important'
                              },
                              '& .MuiSelect-icon': {
                                color: '#FFFFFF !important'
                              },
                              '& .MuiInputBase-input': {
                                color: '#FFFFFF !important'
                              }
                            }
                          }}
                        >
                          {isLoadingEnums ? (
                            <MenuItem disabled>Loading statuses...</MenuItem>
                          ) : (
                            statusOptions.map((status) => (
                              <MenuItem key={status.value} value={status.value}>
                                {status.label}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </div>
                    </FormControl>
                  ) : (
                    <span style={{
                      padding: '8px 16px',
                      backgroundColor: getStatusColor(task.status),
                      color: '#FFFFFF',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      fontFamily: "'Urbanist', Arial, sans-serif",
                      textTransform: 'capitalize',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'default',
                      border: 'none'
                    }}>
                      {task.status || 'to do'}
                    </span>
                  )}
                </div>
              </div>

              {/* Horizontal line between Status and Time row */}
              <div style={{
                width: '100%',
                height: '1px',
                backgroundColor: mode === 'dark' ? '#333' : '#e0e0e0',
                margin: '4px 0',
              }} />

              {/* Time row: timer + Start/End task (below Status, like reference image) */}
              {task && (() => {
                const statusNorm = (task.status || '').trim().toLowerCase().replace(/_/g, ' ');
                const isInProgress = statusNorm === 'in progress' || task.status === 'In Progress';
                const isCompleted = statusNorm === 'done' || statusNorm === 'completed' || task.status === 'Done' || task.status === 'Completed';
                const isSessionActive = task.is_session_active === true;
                const totalMins = task.total_duration_minutes ?? task.total_duration_minut ?? null;
                const completedAt = task.actual_completed_at || null;
                const assignedToId = task.assigned_to || task.assigned_to_user_id || task.assigned_user_id || task.assigned_to_id || task.assigned_user?.id;
                const isAssignedToCurrentUser = currentUserId && assignedToId &&
                  (String(assignedToId) === String(currentUserId) || Number(assignedToId) === Number(currentUserId));
                // Project owner (creator / assigned_by): for other users' tasks, only see timer + End task when session is active (task already started, not ended)
                const isProjectOwnerOrCreator = currentUserId && (
                  task.created_by_user_id === currentUserId ||
                  String(task.created_by) === String(currentUserId) ||
                  String(task.assigned_by) === String(currentUserId)
                );
                const isOwnerViewingOthersTask = isProjectOwnerOrCreator && !isAssignedToCurrentUser;
                const canSeeTimeAndStartEnd = isAssignedToCurrentUser || (isOwnerViewingOthersTask && isSessionActive);
                const liveElapsedMinutes = isSessionActive
                  ? Math.max(0, sessionBaseMinutes + (liveTick - sessionBaseTime) / 60000)
                  : 0;
                const timePassedDisplay = isSessionActive ? formatElapsedHms(liveElapsedMinutes) : (totalMins != null && Number(totalMins) !== 0 ? formatElapsedHms(Number(totalMins)) : '0:00:00');

                if (!isInProgress && !isCompleted) return null;
                if (isInProgress && !canSeeTimeAndStartEnd) return null;

                return (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      minWidth: '120px'
                    }}>
                      <AccessTimeIcon sx={{ fontSize: 18, opacity: 0.9, color: mode === 'dark' ? '#999' : '#666' }} />
                      <span style={{
                        fontSize: '14px',
                        color: mode === 'dark' ? '#999' : '#666',
                        fontFamily: "'Urbanist', Arial, sans-serif",
                        fontWeight: '500'
                      }}>
                        {isCompleted ? 'Time taken' : 'Time'}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      flex: 1,
                      flexWrap: 'wrap'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={handleOpenTimelogModal}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpenTimelogModal(); } }}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '100px',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            backgroundColor: mode === 'dark' ? '#2a2a2a' : '#e8e8e8',
                            border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                            color: mode === 'dark' ? '#fff' : '#222',
                            fontFamily: "'Urbanist', Arial, sans-serif",
                            fontSize: '15px',
                            fontWeight: '700',
                            letterSpacing: '0.02em',
                            cursor: 'pointer',
                          }}
                        >
                          {isCompleted ? formatElapsedHms(Number(totalMins) ?? 0) : timePassedDisplay}
                        </div>
                        {isCompleted && completedAt && (
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 14px',
                            borderRadius: '8px',
                            backgroundColor: mode === 'dark' ? '#252525' : '#f0f0f0',
                            border: `1px solid ${mode === 'dark' ? '#333' : '#ddd'}`,
                            color: mode === 'dark' ? '#b0b0b0' : '#555',
                            fontFamily: "'Urbanist', Arial, sans-serif",
                            fontSize: '13px',
                            fontWeight: '500',
                          }}>
                            <span style={{ color: mode === 'dark' ? '#999' : '#666' }}>Actual completed time:</span>
                            <span>{formatDateTime(completedAt)}</span>
                          </div>
                        )}
                      </div>
                      {isInProgress && isSessionActive && (isAssignedToCurrentUser || isProjectOwnerOrCreator) && (
                          <button
                            type="button"
                            onClick={handleEndTask}
                            disabled={isUpdating}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              background: 'transparent',
                              color: mode === 'dark' ? '#e57373' : '#c62828',
                              border: `2px solid ${mode === 'dark' ? '#e57373' : '#c62828'}`,
                              borderRadius: '8px',
                              padding: '10px 18px',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: isUpdating ? 'not-allowed' : 'pointer',
                              fontFamily: "'Urbanist', Arial, sans-serif",
                              transition: 'all 0.2s ease',
                              letterSpacing: '0.02em',
                              opacity: isUpdating ? 0.7 : 1,
                            }}
                            onMouseEnter={(e) => {
                              if (!isUpdating) {
                                e.currentTarget.style.background = mode === 'dark' ? 'rgba(229, 115, 115, 0.12)' : 'rgba(198, 40, 40, 0.08)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            {isUpdating ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{
                                  width: '16px',
                                  height: '16px',
                                  border: '2px solid rgba(229, 115, 115, 0.5)',
                                  borderTopColor: mode === 'dark' ? '#e57373' : '#c62828',
                                  borderRadius: '50%',
                                  animation: 'spin 0.8s linear infinite',
                                }} />
                                Ending...
                              </span>
                            ) : (
                              <>
                                <StopIcon sx={{ fontSize: 20 }} />
                                End task
                              </>
                            )}
                          </button>
                      )}
                      {isInProgress && !isSessionActive && isAssignedToCurrentUser && (
                          <button
                            type="button"
                            onClick={handleStartTask}
                            disabled={isUpdating}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              background: '#22c55e',
                              color: '#FFFFFF',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '10px 18px',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: isUpdating ? 'not-allowed' : 'pointer',
                              fontFamily: "'Urbanist', Arial, sans-serif",
                              transition: 'all 0.2s ease',
                              boxShadow: '0 2px 6px rgba(34, 197, 94, 0.3)',
                              letterSpacing: '0.02em',
                              opacity: isUpdating ? 0.7 : 1,
                            }}
                            onMouseEnter={(e) => {
                              if (!isUpdating) {
                                e.currentTarget.style.background = '#16a34a';
                                e.currentTarget.style.boxShadow = '0 3px 8px rgba(34, 197, 94, 0.4)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#22c55e';
                              e.currentTarget.style.boxShadow = '0 2px 6px rgba(34, 197, 94, 0.3)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            {isUpdating ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{
                                  width: '16px',
                                  height: '16px',
                                  border: '2px solid rgba(255,255,255,0.6)',
                                  borderTopColor: '#fff',
                                  borderRadius: '50%',
                                  animation: 'spin 0.8s linear infinite',
                                }} />
                                Starting...
                              </span>
                            ) : (
                              <>
                                <PlayArrowIcon sx={{ fontSize: 20 }} />
                                Start task
                              </>
                            )}
                          </button>
                      )}
                    </div>
                  </div>
                );
              })()}

            </div>
          </div>

          {/* Description */}
          <div style={{
            backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
            borderRadius: '12px',
            padding: '24px',
            border: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'hidden'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <img
                  src={DescriptionIcon}
                  alt="Description"
                  style={{
                    width: '18px',
                    height: '18px',
                    filter: 'brightness(0) saturate(100%) invert(70%) sepia(50%) saturate(500%) hue-rotate(160deg) brightness(90%)'
                  }}
                />
                <span style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: mode === 'dark' ? '#FFFFFF' : '#222222',
                  fontFamily: "'Urbanist', Arial, sans-serif"
                }}>
                  Description
                </span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {editDescription ? (
                  <>
                    <button
                      onClick={() => handleSave('description')}
                      disabled={isUpdating}
                      style={{
                        background: isUpdating ? '#999' : '#26C3C0',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        color: '#FFFFFF',
                        cursor: isUpdating ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '14px',
                        opacity: isUpdating ? 0.7 : 1,
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {isUpdating ? (
                        <>
                          <div style={{
                            width: '14px',
                            height: '14px',
                            border: '2px solid #ffffff',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }} />
                          Updating...
                        </>
                      ) : (
                        <>
                          <SaveIcon style={{ fontSize: '16px' }} />
                          Save
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleCancelEdit('description')}
                      style={{
                        background: 'none',
                        border: '1px solid #666',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        color: '#666',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '14px'
                      }}
                    >
                      <CloseIcon style={{ fontSize: '16px' }} />
                      Cancel
                    </button>
                  </>
                ) : (
                  canUserEditTask() && (
                    <button
                      onClick={() => toggleEditMode('description')}
                      style={{
                        background: 'none',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px',
                        color: '#26C3C0',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <EditIcon style={{ fontSize: '18px' }} />
                    </button>
                  )
                )}
              </div>
            </div>
            {editDescription ? (
              <div style={{ marginBottom: '20px' }}>
                <ReactQuill
                  value={editData.description || ''}
                  onChange={(value) => handleEditDataChange('description', value)}
                  placeholder="Enter task description..."
                  readOnly={isLoadingEnums}
                  modules={{
                    toolbar: [
                      ['bold', 'italic'],
                      [{ 'list': 'ordered' }, { 'list': 'bullet' }]
                    ]
                  }}
                  formats={['bold', 'italic', 'list', 'bullet']}
                  theme="snow"
                  style={{
                    backgroundColor: mode === 'dark' ? '#2a2a2a' : '#ffffff',
                    color: mode === 'dark' ? '#ffffff' : '#000000',
                    borderRadius: '8px',
                    border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                    fontFamily: "'Urbanist', Arial, sans-serif"
                  }}
                  sx={{
                    '& .ql-editor': {
                      fontFamily: 'Urbanist, Arial, sans-serif !important',
                      fontSize: '13px !important',
                      lineHeight: '1.4',
                      color: mode === 'dark' ? '#FFFFFF' : '#222222',
                      backgroundColor: 'transparent'
                    },
                    '& .ql-editor *': {
                      fontFamily: 'Urbanist, Arial, sans-serif !important',
                      fontSize: '13px !important'
                    },
                    '& .ql-editor p, & .ql-editor div, & .ql-editor span, & .ql-editor strong, & .ql-editor em, & .ql-editor ul, & .ql-editor ol, & .ql-editor li': {
                      fontFamily: 'Urbanist, Arial, sans-serif !important',
                      fontSize: '13px !important'
                    }
                  }}
                />
              </div>
            ) : (
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <div
                  className="description-scroll"
                  style={{
                    fontSize: '14px',
                    lineHeight: '1.6',
                    color: mode === 'dark' ? '#CCCCCC' : '#666666',
                    margin: '0 0 20px 0',
                    fontFamily: "'Urbanist', Arial, sans-serif",
                    flex: 1,
                    maxHeight: '144px', // 6 lines * 24px line height
                    overflow: 'auto',
                    wordWrap: 'break-word',
                    textAlign: 'justify'
                  }}
                  dangerouslySetInnerHTML={{
                    __html: task.description || 'No description available'
                  }}
                />

                {isUpdating && (
                  <div style={{
                    width: '14px',
                    height: '14px',
                    border: '2px solid #26C3C0',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginTop: '2px'
                  }} />
                )}
              </div>
            )}

            {/* Subtasks from API */}
            {task.subtasks && task.subtasks.length > 0 && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                {task.subtasks.map((subtask, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: `2px solid ${mode === 'dark' ? '#666' : '#999'}`,
                      borderRadius: '3px',
                      backgroundColor: 'transparent'
                    }} />
                    <span style={{
                      fontSize: '14px',
                      color: mode === 'dark' ? '#CCCCCC' : '#666666',
                      fontFamily: "'Urbanist', Arial, sans-serif"
                    }}>
                      {subtask.title || `Subtask ${index + 1}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Attachments from API */}
          <div style={{
            backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
            borderRadius: '12px',
            padding: '24px',
            border: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'hidden'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>

              {/* Success Message Alert */}
              {uploadSuccess && (
                <MessageAlert
                  type="success"
                  show={!!uploadSuccess}
                  message={uploadSuccess}
                  style={{
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    zIndex: 9999,
                    minWidth: '300px'
                  }}
                />
              )}

              {/* Error Message Alert */}
              {uploadError && (
                <div style={{
                  position: 'fixed',
                  top: '20px',
                  right: '20px',
                  backgroundColor: mode === 'dark' ? '#1a0000' : '#ffebee',
                  border: '1px solid #ff4444',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  boxShadow: '0 2px 8px rgba(255, 68, 68, 0.2)',
                  zIndex: 9999,
                  minWidth: '350px',
                  maxWidth: '400px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <WarningIcon style={{
                      color: '#ff4444',
                      fontSize: '20px'
                    }} />
                    <span style={{
                      color: mode === 'dark' ? '#ffffff' : '#d32f2f',
                      fontSize: '14px',
                      fontFamily: "'Urbanist', Arial, sans-serif",
                      lineHeight: '1.4'
                    }}>
                      {uploadError}
                    </span>
                  </div>
                </div>
              )}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <img
                  src={AttachIcon}
                  alt="Attachments"
                  style={{
                    width: '18px',
                    height: '18px',
                    filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(40%)',
                    opacity: 0.7
                  }}
                />
                <span style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: mode === 'dark' ? '#FFFFFF' : '#222222',
                  fontFamily: "'Urbanist', Arial, sans-serif"
                }}>
                  Attachments
                </span>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {/* Download All Button */}
                {task?.attachments && task.attachments.length > 0 && (
                  <button
                    onClick={handleDownloadAll}
                    disabled={isDownloadingAll}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      backgroundColor: isDownloadingAll ? '#999' : '#26C3C0',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: isDownloadingAll ? 'not-allowed' : 'pointer',
                      fontFamily: "'Urbanist', Arial, sans-serif",
                      transition: 'background-color 0.2s ease',
                      opacity: isDownloadingAll ? 0.7 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!isDownloadingAll) {
                        e.target.style.backgroundColor = '#1f9e9b';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isDownloadingAll) {
                        e.target.style.backgroundColor = '#26C3C0';
                      }
                    }}
                  >
                    {isDownloadingAll ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid #ffffff',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <DownloadIcon style={{ fontSize: '18px' }} />
                        Download All
                      </>
                    )}
                  </button>
                )}
                {/* Add Button */}
                {hasAddPermission('task') && (
                  <button
                    onClick={() => document.getElementById('fileInput').click()}
                    disabled={isUploading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      backgroundColor: isUploading ? '#999' : '#26C3C0',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: isUploading ? 'not-allowed' : 'pointer',
                      fontFamily: "'Urbanist', Arial, sans-serif",
                      transition: 'background-color 0.2s ease',
                      opacity: isUploading ? 0.7 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!isUploading) {
                        e.target.style.backgroundColor = '#1f9e9b';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isUploading) {
                        e.target.style.backgroundColor = '#26C3C0';
                      }
                    }}
                  >
                    {isUploading ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid #ffffff',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <AddIcon style={{ fontSize: '18px' }} />
                        Add
                      </>
                    )}
                  </button>
                )}
              </div>
              {/* Hidden file input */}
              <input
                id="fileInput"
                type="file"
                style={{ display: 'none' }}
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.xls,.xlsx,.ppt,.pptx"
                onChange={handleFileSelect}
              />
            </div>

            {/* Attachments from API */}
            <div
              className="task-details-attachments-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '16px',
                width: '100%',
                maxWidth: '100%',
                overflowX: 'hidden',
                boxSizing: 'border-box'
              }}
            >
              {task.attachments && task.attachments.length > 0 ? (
                task.attachments.map((attachment, index) => (
                  <div key={attachment.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px',
                    backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
                    borderRadius: '8px',
                    border: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                    overflow: 'hidden'
                  }}>
                    {getFileIcon(attachment.file_name)}
                    <div style={{
                      flex: 1,
                      minWidth: 0,
                      width: 0, // Force flex item to respect container width
                      maxWidth: '100%',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '500',
                        color: mode === 'dark' ? '#FFFFFF' : '#222222',
                        marginBottom: '4px',
                        fontFamily: "'Urbanist', Arial, sans-serif",
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        width: '100%',
                        maxWidth: '100%'
                      }}
                        title={attachment.file_name}
                      >
                        {attachment.file_name && attachment.file_name.length > 15
                          ? `${attachment.file_name.substring(0, 15)}...`
                          : attachment.file_name}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: mode === 'dark' ? '#999' : '#666',
                        fontFamily: "'Urbanist', Arial, sans-serif"
                      }}>
                        {formatDateTime(attachment.uploaded_at)}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '8px'
                    }}>
                      <button
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: viewingAttachmentId === attachment.id ? 'wait' : 'pointer',
                          padding: '8px',
                          borderRadius: '4px',
                          color: mode === 'dark' ? '#FFFFFF' : '#666',
                          transition: 'background-color 0.2s ease',
                          position: 'relative'
                        }}
                        onClick={() => handleViewAttachment(attachment)}
                        disabled={viewingAttachmentId === attachment.id}
                      >
                        {viewingAttachmentId === attachment.id ? (
                          <div style={{
                            width: '18px',
                            height: '18px',
                            border: '2px solid #26C3C0',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }} />
                        ) : (
                          <img
                            src={EyeOpenIcon}
                            alt="View"
                            style={{
                              width: '18px',
                              height: '18px',
                              filter: 'brightness(0) saturate(100%) invert(70%) sepia(50%) saturate(500%) hue-rotate(160deg) brightness(90%) contrast(120%)'
                            }}
                          />
                        )}
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  padding: '24px',
                  color: mode === 'dark' ? '#999' : '#666',
                  fontFamily: "'Urbanist', Arial, sans-serif"
                }}>
                  No attachments found
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Side - Comments Component (35% width) */}
        <div
          className="task-details-right"
          style={{
            flex: '0 0 calc(35% - 12px)',
            width: 'calc(35% - 12px)',
            maxWidth: 'calc(35% - 12px)',
            minWidth: 0,
            alignSelf: 'stretch',
            overflowX: 'hidden',
            overflowY: 'visible',
            boxSizing: 'border-box'
          }}
        >
          <div style={{
            width: '100%',
            maxWidth: '100%',
            height: '100%',
            overflowX: 'hidden',
            overflowY: 'visible',
            boxSizing: 'border-box',
            minWidth: 0
          }}>
            <TaskDetailCommentBlock
              taskId={task.id}
              comments={task.comments || []}
              currentUserId={currentUserId}
              employees={employees}
            />
          </div>
        </div>
      </div>

      {/* Time logs modal */}
      {timelogModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.5)',
            padding: '24px',
          }}
          onClick={handleCloseTimelogModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="timelog-modal-title"
        >
          <div
            style={{
              backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              maxWidth: '560px',
              width: '100%',
              maxHeight: '85vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '20px 24px',
              borderBottom: `1px solid ${mode === 'dark' ? '#333' : '#eee'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <h2 id="timelog-modal-title" style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: mode === 'dark' ? '#fff' : '#222',
                fontFamily: "'Urbanist', Arial, sans-serif",
              }}>
                Time logs
              </h2>
              <button
                type="button"
                onClick={handleCloseTimelogModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: mode === 'dark' ? '#999' : '#666',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                aria-label="Close"
              >
                <CloseIcon sx={{ fontSize: 24 }} />
              </button>
            </div>
            <div style={{
              padding: '20px 24px',
              overflowY: 'auto',
              flex: 1,
            }}>
              {timelogsLoading ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '40px',
                  color: mode === 'dark' ? '#999' : '#666',
                  fontFamily: "'Urbanist', Arial, sans-serif",
                }}>
                  <span style={{
                    display: 'inline-block',
                    width: '24px',
                    height: '24px',
                    border: `2px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                    borderTopColor: '#26C3C0',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                    marginRight: '12px',
                  }} />
                  Loading time logs...
                </div>
              ) : timelogs.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: mode === 'dark' ? '#999' : '#666',
                  fontFamily: "'Urbanist', Arial, sans-serif",
                  fontSize: '14px',
                }}>
                  No time logs for this task yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {timelogs.map((log, index) => (
                    <div
                      key={log.id || index}
                      style={{
                        padding: '10px 12px',
                        borderRadius: '8px',
                        backgroundColor: mode === 'dark' ? '#252525' : '#f8f9fa',
                        border: `1px solid ${mode === 'dark' ? '#333' : '#eee'}`,
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '6px',
                      }}>
                        <span style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          color: mode === 'dark' ? '#fff' : '#222',
                          fontFamily: "'Urbanist', Arial, sans-serif",
                        }}>
                          {log.user_name || 'Unknown'}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: mode === 'dark' ? '#26C3C0' : '#1a9a97',
                            fontFamily: "'Urbanist', Arial, sans-serif",
                          }}>
                            {formatElapsedHms(log.duration_minutes ?? 0)}
                          </span>
                          {canCurrentUserEditTimelogs && (
                            <button
                              type="button"
                              style={{
                                padding: '4px 8px',
                                fontSize: '11px',
                                borderRadius: '6px',
                                border: '1px solid',
                                borderColor: mode === 'dark' ? '#444' : '#ccc',
                                backgroundColor: mode === 'dark' ? '#2a2a2a' : '#ffffff',
                                color: mode === 'dark' ? '#e0e0e0' : '#333',
                                cursor: 'pointer',
                                fontFamily: "'Urbanist', Arial, sans-serif",
                              }}
                              onClick={() => handleStartEditTimelog(log, index)}
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </div>
                      {editingTimelog && editingTimelog.id === (log.id || index) ? (
                        <div style={{
                          marginTop: '8px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          fontFamily: "'Urbanist', Arial, sans-serif",
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '12px', color: mode === 'dark' ? '#ccc' : '#444' }}>
                              Start time
                            </label>
                            <input
                              type="datetime-local"
                              value={editingTimelog.startInput}
                              onChange={(e) =>
                                setEditingTimelog((prev) => prev ? { ...prev, startInput: e.target.value } : prev)
                              }
                              style={{
                                padding: '6px 8px',
                                borderRadius: '6px',
                                border: `1px solid ${mode === 'dark' ? '#555' : '#ccc'}`,
                                backgroundColor: mode === 'dark' ? '#1a1a1a' : '#fff',
                                color: mode === 'dark' ? '#fff' : '#222',
                                fontSize: '12px',
                              }}
                            />
                            </div>
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '12px', color: mode === 'dark' ? '#ccc' : '#444' }}>
                              End time
                            </label>
                            <input
                              type="datetime-local"
                              value={editingTimelog.endInput || ''}
                              onChange={(e) =>
                                setEditingTimelog((prev) => prev ? { ...prev, endInput: e.target.value } : prev)
                              }
                              style={{
                                padding: '6px 8px',
                                borderRadius: '6px',
                                border: `1px solid ${mode === 'dark' ? '#555' : '#ccc'}`,
                                backgroundColor: mode === 'dark' ? '#1a1a1a' : '#fff',
                                color: mode === 'dark' ? '#fff' : '#222',
                                fontSize: '12px',
                              }}
                            />
                            </div>
                          </div>
                          {timelogEditError && (
                            <div style={{ fontSize: '12px', color: '#e53935' }}>
                              {timelogEditError}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                            <button
                              type="button"
                              onClick={handleSaveTimelogEdit}
                              disabled={isSavingTimelog}
                              style={{
                                padding: '6px 10px',
                                borderRadius: '6px',
                                border: 'none',
                                backgroundColor: '#22c55e',
                                color: '#fff',
                                fontSize: '12px',
                                cursor: isSavingTimelog ? 'not-allowed' : 'pointer',
                                fontFamily: "'Urbanist', Arial, sans-serif",
                              }}
                            >
                              {isSavingTimelog ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              type="button"
                              onClick={handleCancelEditTimelog}
                              disabled={isSavingTimelog}
                              style={{
                                padding: '6px 10px',
                                borderRadius: '6px',
                                border: `1px solid ${mode === 'dark' ? '#555' : '#ccc'}`,
                                backgroundColor: 'transparent',
                                color: mode === 'dark' ? '#e0e0e0' : '#333',
                                fontSize: '12px',
                                cursor: isSavingTimelog ? 'not-allowed' : 'pointer',
                                fontFamily: "'Urbanist', Arial, sans-serif",
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          fontSize: '12px',
                          color: mode === 'dark' ? '#b0b0b0' : '#555',
                          fontFamily: "'Urbanist', Arial, sans-serif",
                          lineHeight: 1.4,
                        }}>
                          Start: {formatDateTime(log.start_time)} — End: {log.end_time ? formatDateTime(log.end_time) : '—'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default TaskDetails;
